import { useSwatches } from "@hooks/useSwatches";
import { FilterContext } from "~/providers/FilterProvider";
import { ComponentProps } from "@ts/components";
import { SettingSwatches } from "@ts/sanity";
import { SearchResultSwatch } from "@ts/search";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from "react";

export type FilterSectionSwatchInputProps = ComponentProps & {
  options: SearchResultSwatch[];
  value: Record<string, string> | string | number;
  handleChange: (key: string) => void;
};

export type FilterSectionSwatchOutputProps = Omit<
  FilterSectionSwatchInputProps,
  "options"
> & {
  setShowMore: Dispatch<SetStateAction<boolean>>;
  showMore: boolean;
  handleScroll: () => void;
  options: {
    doc_count: number;
    key: string;
    swatch: SettingSwatches;
  }[];
};

const prepareSwatchOptions = (
  options: SearchResultSwatch[],
  getSwatch: (key: string) => any,
) =>
  options
    ?.sort((a, b) => a?.key?.localeCompare(b?.key))
    ?.map(({ key, doc_count }) => ({
      key,
      doc_count,
      swatch: getSwatch(key?.toLowerCase()),
    }));

export const withFilterSectionSwatch =
  (Component: FC<FilterSectionSwatchOutputProps>) =>
  ({
    name = "FilterSectionSwatch",
    options,
    value,
    handleChange,
  }: FilterSectionSwatchInputProps) => {
    const { handleScroll } = useContext(FilterContext);
    const [showMore, setShowMore] = useState(false);
    const { getSwatch } = useSwatches();
    const swatchOptions = prepareSwatchOptions(options, getSwatch) || [];

    if (!swatchOptions) {
      return null;
    }

    Component.displayName = name;
    return (
      <Component
        options={swatchOptions}
        handleChange={handleChange}
        value={value}
        showMore={showMore}
        setShowMore={setShowMore}
        handleScroll={handleScroll}
      />
    );
  };
